import { Client, xml } from "@xmpp/client"
import { Element } from "@xmpp/xml"
import iqMeasure from "../instrumentation/iqMeasure"
import {
  startCollecting,
  collectResults,
  discardQuery,
} from "../lib/streamedSearchCollector"
import { SearchHit } from "../lib/searchResultParser"
import SettingsParser from "../lib/settingsParser"
import { DataFormsParser } from "../lib/formParser"
import { Route2g } from "../reducers/routes2gSlice"
import { extractVhostFromJid } from "../jidUtils"
import { UpsertRouteError } from "@/hooks/useDriverContext"
import XmppPubSubApi from "./xmppPubSubApi"
import { UploadedFile } from "@/hooks/useExtractDataFromOrder"
import { GBOX_TIMELINE_COMMAND_NODE } from "@/lib/gboxTimeline"
import {
  NAVIGATOR_TIMELINE_COMMAND_NODE,
  NAVIGATOR_TIMELINE_NS,
  NAVIGATOR_TIMELINE_PAGE_ELEMENT,
} from "@/lib/navigatorTimeline"
import { NAVIGATOR_VEHICLE_DETAILS_COMMAND_NODE } from "@/lib/navigatorVehicleDetails"
import { NAVIGATOR_ATTACHMENT_COMMAND_NODE } from "@/lib/navigatorAttachment"
import { PLATFORM_SCIENCE_TIMELINE_COMMAND_NODE } from "@/lib/platformScienceTimeline"

const PUSHNOT_ADDRESS = "pushnot.global"
const WIZARD = "wizard"
const TMS_CARS_COMMAND_NODE = "get_tms_cars"
const TMS_TRAILERS_COMMAND_NODE = "get_tms_trailers"
const VEHICLE_APPROACH_COMMAND_NODE = "get_vehicle_approach_distances"
const ROSTER_SYNC_STATUS_COMMAND_NODE = "roster_sync_status"
const RUN_ROSTER_SYNC_COMMAND_NODE = "run_roster_sync"
const GET_ACCOUNT_CONFIG_COMMAND_NODE = "get_account_config"
const SET_ACCOUNT_CONFIG_COMMAND_NODE = "set_account_config"
// Fleet-wide timeline requests (Planner role) can fan out to hundreds of
// vehicles server-side — comfortably above the @xmpp/iq default of 30s even
// after backend caching, so these three get a longer allowance.
const TIMELINE_VIEW_IQ_TIMEOUT_MS = 90000
const TRANSLATE_TEXT_COMMAND_NODE = "translate_text"
const TRANSLATE_TEXT_IQ_TIMEOUT_MS = 20000

// plan_route computes up to 4 alternatives per segment through PTV, including
// tolls across multiple countries.  With stale route-handle recovery the
// server-side work regularly takes 30-40s, exceeding the @xmpp/iq 30s default.
const PLAN_ROUTE_IQ_TIMEOUT_MS = 45000

// Marcos can take over a minute to create an order (gps waits up to 60 s for
// it, then syncs it and attaches the PDF). At the 30 s default the browser
// said "Connection error with Marcos" while the order was still being made,
// and the dispatcher clicked again (oskar 2026-11-03).
const CREATE_NAVI_ORDER_IQ_TIMEOUT_MS = 180000

export type PushToken =
  | {
      data: string
      type: string
      supportsBackground?: boolean
    }
  | PushSubscription

export type AvoidType =
  | "tollRoads"
  | "motorways"
  | "ferries"
  | "unpavedRoads"
  | "carpools"
  | "alreadyUsedRoads"
  | "borderCrossings"
  | "tunnels"
  | "carTrains"
  | "lowEmissionZones"

export type TravelMode = "car" | "truck"

export type LoadType =
  | "otherHazmatExplosive"
  | "otherHazmatGeneral"
  | "otherHazmatHarmfulToWater"

export type EmissionType =
  | "none"
  | "euro1"
  | "euro2"
  | "euro3"
  | "euro4"
  | "euro5"
  | "euro6"

export type CO2ClassType = "none" | "1" | "2" | "3" | "4" | "5" | "6"

export type NaviSelectionInput = {
  carrierId?: number
  carrierName?: string
  customerId?: number
  customerName?: string
  orderingPartyAddressId?: number
  driver1Id?: number
  freight?: number
  freightCurrency?: string
  carrierCharge?: number
  carrierChargeCurrency?: string
  externalNumber?: string
  customerReference?: string
  refNumber?: string
  transportNumber?: string
  paymentTermsDays?: number
  loadedKm?: number
  customerNip?: string
  customerAddressId?: number
  carrierNip?: string
  carrierAddressId?: number
  createdById?: number
  createdByFirstName?: string
  createdByLastName?: string
  forwarderId?: number
  forwarderFirstName?: string
  forwarderLastName?: string
  trailer?: string
  trailerId?: number
  truck?: string
  truckId?: number
  isAdr?: boolean
}

export type CarRouteConfig = {
  width?: string
  height?: string
  length?: string
  weight?: string
  maxSpeed?: string
  avoid?: AvoidType[]
  travelMode?: TravelMode
  loadType?: LoadType[]
  emissionType?: EmissionType
  axleCount?: string
  co2class?: string
  fuelConsumption?: string
}

// Mirrors the lambda's BillingMetadata pydantic model
export type DeliveryOrderBillingMetadata = {
  original_transport_documents_required?: boolean
  original_transport_documents_notes?: string
  digital_transport_documents_notes?: string
  payment_terms_days?: number
  payment_terms_notes?: string
  client_platform_upload_required?: boolean | null
  client_platform_upload_notes?: string
  important_settlement_notes?: string
}

// Mirrors the lambda's DocumentMetadata / BillingDocumentMetadata pydantic model
// (stops are omitted — they travel as individual route stop items)
export type DeliveryOrderMetadata = {
  ref_number?: string
  notes?: string
  pallet_exchange_notes?: string
  // Yes/no behind pallet_exchange_notes; undefined when the order says nothing.
  pallet_exchange?: boolean
  // Required cargo temperature range in °C; a single set point fills both.
  temperature_min?: number
  temperature_max?: number
  weight?: string
  price_netto_eur?: number
  nip?: string
  order_number?: string
  transport_number?: string
  payment_deadline?: string
  customer_name?: string
  customer_address?: string
  customer_postal_code?: string
  customer_contact?: string
  customer_email?: string
  customer_phone?: string
  recipient_name?: string
  dispatch_location?: string
  driver_name?: string
  car_plates?: string
  trailer_plates?: string
  billing?: DeliveryOrderBillingMetadata
  source_file?: string
  loaded_kilometers?: string
  empty_kilometers?: string
}

export type RouteAlertConfig = {
  temperature_main_alert_level_min?: string
  temperature_main_alert_level_max?: string
  temperature_additional_alert_level_min?: string
  temperature_additional_alert_level_max?: string
}

type GetRoomsItem = {
  jid: string
  name: string
  groups: string[]
}

const deliveryOrderMetaField = (
  varName: string,
  value: string | number | boolean | null | undefined,
) => {
  if (value === undefined || value === null || value === "") return null
  return xml(
    "field",
    { var: varName, type: "text-single" },
    xml("value", {}, String(value)),
  )
}

const buildDeliveryOrderMetadataFields = (meta: DeliveryOrderMetadata) => {
  const fields = [
    deliveryOrderMetaField("ref_number", meta.ref_number),
    deliveryOrderMetaField("notes", meta.notes),
    deliveryOrderMetaField("pallet_exchange_notes", meta.pallet_exchange_notes),
    deliveryOrderMetaField("pallet_exchange", meta.pallet_exchange),
    deliveryOrderMetaField("temperature_min", meta.temperature_min),
    deliveryOrderMetaField("temperature_max", meta.temperature_max),
    deliveryOrderMetaField("weight", meta.weight),
    deliveryOrderMetaField("price_netto_eur", meta.price_netto_eur),
    deliveryOrderMetaField("nip", meta.nip),
    deliveryOrderMetaField("order_number", meta.order_number),
    deliveryOrderMetaField("transport_number", meta.transport_number),
    deliveryOrderMetaField("payment_deadline", meta.payment_deadline),
    deliveryOrderMetaField("customer_name", meta.customer_name),
    deliveryOrderMetaField("customer_address", meta.customer_address),
    deliveryOrderMetaField("customer_postal_code", meta.customer_postal_code),
    deliveryOrderMetaField("customer_contact", meta.customer_contact),
    deliveryOrderMetaField("customer_email", meta.customer_email),
    deliveryOrderMetaField("customer_phone", meta.customer_phone),
    deliveryOrderMetaField("recipient_name", meta.recipient_name),
    deliveryOrderMetaField("dispatch_location", meta.dispatch_location),
    deliveryOrderMetaField("driver_name", meta.driver_name),
    deliveryOrderMetaField("car_plates", meta.car_plates),
    deliveryOrderMetaField("trailer_plates", meta.trailer_plates),
    deliveryOrderMetaField("source_file", meta.source_file),
    deliveryOrderMetaField("loaded_kilometers", meta.loaded_kilometers),
    deliveryOrderMetaField("empty_kilometers", meta.empty_kilometers),
  ]

  if (meta.billing) {
    const b = meta.billing
    fields.push(
      deliveryOrderMetaField(
        "billing_orig_docs_required",
        b.original_transport_documents_required,
      ),
      deliveryOrderMetaField(
        "billing_orig_docs_notes",
        b.original_transport_documents_notes,
      ),
      deliveryOrderMetaField(
        "billing_digital_docs_notes",
        b.digital_transport_documents_notes,
      ),
      deliveryOrderMetaField(
        "billing_payment_terms_days",
        b.payment_terms_days,
      ),
      deliveryOrderMetaField(
        "billing_payment_terms_notes",
        b.payment_terms_notes,
      ),
      deliveryOrderMetaField(
        "billing_platform_upload_required",
        b.client_platform_upload_required,
      ),
      deliveryOrderMetaField(
        "billing_platform_upload_notes",
        b.client_platform_upload_notes,
      ),
      deliveryOrderMetaField(
        "billing_important_notes",
        b.important_settlement_notes,
      ),
    )
  }

  return fields.filter((f): f is NonNullable<typeof f> => f !== null)
}

export const buildStops = (
  stops: Route2g["stops"],
  options?: {
    useLatLon?: boolean
    sendRouteHandle?: boolean
  },
) =>
  stops.map((stop) =>
    xml(
      "item",
      {},
      xml(
        "field",
        { var: "id", type: "text-single" },
        xml("value", {}, stop.id || ""),
      ),
      xml(
        "field",
        { var: "address", type: "text-single" },
        xml(
          "value",
          {},
          options?.useLatLon && stop.location.lat && stop.location.lon
            ? `${stop.location.lat},${stop.location.lon}`
            : stop.location.formattedAddress || "",
        ),
      ),
      xml(
        "field",
        { var: "company_name", type: "text-single" },
        xml("value", {}, stop.location.companyName || ""),
      ),
      xml(
        "field",
        { var: "postal_code", type: "text-single" },
        xml("value", {}, stop.location.postalCode || ""),
      ),
      xml(
        "field",
        { var: "address-updated", type: "boolean" },
        xml("value", {}, stop.addressUpdated === true ? "true" : "false"),
      ),
      xml(
        "field",
        { var: "msg-id", type: "text-single" },
        xml("value", {}, stop.msgId || ""),
      ),
      xml(
        "field",
        { var: "action", type: "text-single" },
        xml("value", {}, stop.action || "other"),
      ),
      xml(
        "field",
        { var: "action_reference_number", type: "text-single" },
        xml("value", {}, stop.action_reference_number || ""),
      ),
      xml(
        "field",
        { var: "ref", type: "text-single" },
        xml("value", {}, stop.ref || ""),
      ),
      xml(
        "field",
        { var: "scheduled_arrival_time", type: "text-single" },
        xml("value", {}, stop.scheduled_arrival_time || ""),
      ),
      xml(
        "field",
        { var: "delivery_order_timestamp", type: "text-single" },
        xml("value", {}, stop.deliveryOrderTimestamp || ""),
      ),
      ...(options?.sendRouteHandle
        ? [
            xml(
              "field",
              { var: "route_handle", type: "text-single" },
              xml("value", {}, stop.route_handle || ""),
            ),
          ]
        : []),
      ...(stop.avoid !== undefined
        ? [
            xml(
              "field",
              { var: "avoid", type: "text-multi" },
              ...stop.avoid.map((avoid) => xml("value", {}, avoid)),
            ),
          ]
        : []),
      ...(stop.source_metadata !== undefined
        ? [
            xml(
              "field",
              { var: "source_metadata", type: "text-single" },
              xml("value", {}, JSON.stringify(stop.source_metadata)),
            ),
          ]
        : []),
    ),
  )

export type PromptCategory = {
  name: string
  icon: string
  title: string
  shortDescription: string
  prompts: string[]
}

export type PromptSuggestions = {
  popular: string[]
  groups: PromptCategory[]
}

export type RosterSyncStatus = {
  allowed: boolean
  readyIn: number
}

export type RosterSyncRunStatus =
  | "started"
  | "cooldown"
  | "already_running"
  | "failed"

export type RosterSyncRunResult = {
  status: RosterSyncRunStatus
  readyIn: number
  reason: string
}

export const EMPTY_PROMPT_SUGGESTIONS: PromptSuggestions = {
  popular: [],
  groups: [],
}

export type GeocodeStopInput = {
  id: string
  address: string
  companyName?: string
  postalCode?: string
}

const naviField = (name: string, value: unknown) =>
  xml(
    "field",
    { var: name, type: "text-single" },
    xml(
      "value",
      {},
      value === undefined || value === null ? "" : String(value),
    ),
  )

// The dispatcher creates the Marcos order before the route is saved to the
// driver chat, so the backend can't read stops from MUC. Send them inline as
// <item> rows carrying the structured address fields the Marcos mapper needs
// (lat/lng + street/locality/country), so no server-side geocoding is needed.
const buildNaviStops = (stops: Route2g["stops"]) =>
  stops.map((stop) =>
    xml(
      "item",
      {},
      naviField("id", stop.id),
      naviField("action", stop.action),
      naviField("lat", stop.location.lat),
      naviField("lng", stop.location.lon),
      naviField("street", stop.location.street),
      naviField("locality", stop.location.locality),
      naviField("postal_code", stop.location.postalCode),
      naviField("country", stop.location.country),
      naviField("country_code", stop.location.countryCode),
      naviField("address", stop.location.formattedAddress),
      naviField("company_name", stop.location.companyName),
      naviField("scheduled_arrival_time", stop.scheduled_arrival_time),
      naviField("scheduled_hour_min", stop.scheduled_hour_min),
      naviField("scheduled_hour_max", stop.scheduled_hour_max),
      naviField("weight_kg", stop.weight_kg),
      naviField("pallet_count", stop.pallet_count),
      naviField("reference_number", stop.action_reference_number),
    ),
  )

const naviSelectionForm = (
  driverJid: string,
  s: NaviSelectionInput,
  transportId?: number,
  stops?: Route2g["stops"],
  fileUrl?: string,
  fileName?: string,
) => {
  const fields = [
    naviField("driver_jid", driverJid),
    naviField("carrier_id", s.carrierId),
    naviField("carrier_name", s.carrierName),
    naviField("customer_id", s.customerId),
    naviField("customer_name", s.customerName),
    naviField("ordering_party_address_id", s.orderingPartyAddressId),
    naviField("driver1_id", s.driver1Id),
    naviField("freight", s.freight),
    naviField("freight_currency", s.freightCurrency),
    naviField("carrier_charge", s.carrierCharge),
    naviField("carrier_charge_currency", s.carrierChargeCurrency),
    naviField("external_number", s.externalNumber),
    naviField("customer_reference", s.customerReference),
    naviField("ref_number", s.refNumber),
    naviField("transport_number", s.transportNumber),
    naviField("payment_terms_days", s.paymentTermsDays),
    naviField("loaded_km", s.loadedKm),
    naviField("customer_nip", s.customerNip),
    naviField("customer_address_id", s.customerAddressId),
    naviField("carrier_nip", s.carrierNip),
    naviField("carrier_address_id", s.carrierAddressId),
    naviField("created_by_id", s.createdById),
    naviField("created_by_first_name", s.createdByFirstName),
    naviField("created_by_last_name", s.createdByLastName),
    naviField("forwarder_id", s.forwarderId),
    naviField("forwarder_first_name", s.forwarderFirstName),
    naviField("forwarder_last_name", s.forwarderLastName),
    naviField("trailer", s.trailer),
    naviField("trailer_id", s.trailerId),
    naviField("truck", s.truck),
    naviField("truck_id", s.truckId),
    naviField("adr", s.isAdr ? "1" : "0"),
  ]
  if (transportId !== undefined)
    fields.push(naviField("transport_id", transportId))
  if (fileUrl !== undefined) fields.push(naviField("file_url", fileUrl))
  if (fileName !== undefined) fields.push(naviField("file_name", fileName))
  return xml(
    "x",
    { xmlns: "jabber:x:data", type: "form" },
    ...fields,
    ...(stops ? buildNaviStops(stops) : []),
  )
}

// The roster-sync and account-config commands answer with a flat result form,
// so the stanza differs only by node and the optional submitted fields.
const accountConfigComponentCommand = async (
  client: Client,
  node: string,
  input: Record<string, string> = {},
): Promise<Record<string, string>> => {
  const { iqCaller } = client
  const measuredIqCaller = iqMeasure(iqCaller)
  const vhost = extractVhostFromJid(client!.jid!.toString())
  const result = await measuredIqCaller.request(
    xml(
      "iq",
      { from: client.jid, type: "set", to: `wizard@${vhost}/account_config` },
      xml(
        "command",
        {
          node,
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        ...(Object.keys(input).length > 0
          ? [
              xml(
                "x",
                { xmlns: "jabber:x:data", type: "submit" },
                ...Object.entries(input).map(([name, value]) =>
                  xml(
                    "field",
                    { var: name, type: "text-single" },
                    xml("value", {}, value),
                  ),
                ),
              ),
            ]
          : []),
      ),
    ),
  )
  const command = result.getChild("command")
  const form = command ? DataFormsParser.parse(command) : undefined
  const values: Record<string, string> = {}
  Object.entries(form?.fields ?? {}).forEach(([name, field]) => {
    values[toCamelCase(name)] = field.values[0] ?? ""
  })
  return values
}

const toCamelCase = (name: string): string =>
  name.replace(/_([a-z])/g, (_match, letter: string) => letter.toUpperCase())

// The dispatcher mapping comes back as one text-multi field per dispatcher:
// var is the dispatcher's JID, the values are their chats. Field names are
// JIDs, so they are used as they arrive - no camel-casing.
const DISPATCHER_CHATS_COMMAND_NODE = "dispatcher_chats"
const ASSIGN_CHAT_DISPATCHER_NODE = "assign_chat_dispatcher"

export type UserPermissions = {
  permissions: string[]
  /** The account has a fleet manager, see permissionsSlice. */
  accountManaged: boolean
}

const dispatcherChatsCommand = async (
  client: Client,
): Promise<Record<string, string[]>> => {
  const { iqCaller } = client
  const measuredIqCaller = iqMeasure(iqCaller, DISPATCHER_CHATS_COMMAND_NODE)
  const vhost = extractVhostFromJid(client!.jid!.toString())
  const result = await measuredIqCaller.request(
    xml(
      "iq",
      { from: client.jid, type: "set", to: `wizard@${vhost}/account_config` },
      xml("command", {
        node: DISPATCHER_CHATS_COMMAND_NODE,
        action: "execute",
        xmlns: "http://jabber.org/protocol/commands",
      }),
    ),
  )
  const command = result.getChild("command")
  const form = command ? DataFormsParser.parse(command) : undefined
  const mapping: Record<string, string[]> = {}
  Object.entries(form?.fields ?? {}).forEach(([dispatcherJid, field]) => {
    mapping[dispatcherJid] = field.values.filter(Boolean)
  })
  return mapping
}

export const parseTranslatedText = (response: Element): string | undefined => {
  const command = response.getChild("command")
  const form = command && DataFormsParser.parse(command)

  // The wizard returns an empty string when no translation was needed.
  return form?.fields?.["translated_text"]?.values[0] || undefined
}

const AdHocCommands = {
  sendPushNotificationsSubscription: (
    client: Client,
    subscription: PushToken,
  ): Promise<Element> => {
    const { iqCaller } = client

    const measuredIqCaller = iqMeasure(iqCaller)
    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: PUSHNOT_ADDRESS,
        },
        xml(
          "command",
          {
            node: "set_subscription",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("json", "urn:xmpp:json:0", JSON.stringify(subscription)),
        ),
      ),
    )
  },

  getPushNotificationsSubscription: async (
    client: Client,
  ): Promise<PushToken | undefined> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)

    const result = await measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: PUSHNOT_ADDRESS,
        },
        xml("command", {
          node: "get_subscription",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        }),
      ),
    )
    return SettingsParser.parsePushSubscription(result)
  },

  disablePushNotificationsSubscription: async (
    client: Client,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller, "disable_subscription")

    return await measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: PUSHNOT_ADDRESS,
        },
        xml("command", {
          node: "disable_subscription",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        }),
      ),
    )
  },

  upsertRoute2g: async (
    client: Client,
    to: string,
    route: Route2g,
    options?: {
      useLatLon?: boolean
      sendRouteHandle?: boolean
      orderMetadata?: DeliveryOrderMetadata
    },
  ): Promise<Element | UpsertRouteError> => {
    const { iqCaller } = client
    const vhost = extractVhostFromJid(to)
    const targetApi = `wizard@${vhost}/gps`
    const measuredIqCaller = iqMeasure(iqCaller)
    const result = await measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "upsert_route_stops",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "reported",
              {},
              xml("field", { var: "address", type: "text-single" }),
              xml("field", { var: "address-updated", type: "boolean" }),
              xml("field", { var: "id", type: "text-single" }),
              xml("field", { var: "msg-id", type: "text-single" }),
              xml("field", { var: "action", type: "text-single" }),
              xml("field", { var: "ref", type: "text-single" }),
              xml("field", { var: "route_handle", type: "text-single" }),
              xml("field", {
                var: "scheduled_arrival_time",
                type: "text-single",
              }),
              xml("field", {
                var: "action_reference_number",
                type: "text-single",
              }),
              xml("field", {
                var: "delivery_order_timestamp",
                type: "text-single",
              }),
              xml("field", {
                var: "avoid",
                type: "text-multi",
              }),
              xml("field", {
                var: "source_metadata",
                type: "text-single",
              }),
            ),
            ...buildStops(route.stops, {
              useLatLon: options?.useLatLon,
              sendRouteHandle: options?.sendRouteHandle,
            }),
            xml(
              "field",
              { var: "driver_jid", type: "jid-single" },
              xml("value", {}, to),
            ),
            ...(options?.orderMetadata !== undefined
              ? buildDeliveryOrderMetadataFields(options.orderMetadata)
              : []),
          ),
        ),
      ),
    )

    if (
      result.getChild("command")?.getChild("note")?.getAttr("type") === "error"
    ) {
      return {
        error: true,
        message: result.getChild("command")?.getChild("note")?.getText(),
      }
    }

    return result
  },

  getCommandsList: (client: Client, to: string): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller, "disco_info_commands")

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "get",
          to: to,
        },
        xml("query", {
          node: "http://jabber.org/protocol/commands",
          xmlns: "http://jabber.org/protocol/disco#items",
        }),
      ),
    )
  },
  getFuelStations: async (client: Client): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())

    const request = (to: string) =>
      measuredIqCaller.request(
        xml(
          "iq",
          {
            type: "set",
            to,
          },
          xml("command", {
            node: "get_fuel_stations",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          }),
        ),
      )

    try {
      return await request(`transchat@${vhost}/transchat`)
    } catch (error) {
      // Vhosts without the (deprecated) transchat service serve fuel
      // stations from the wizard instead.
      console.debug(
        "transchat fuel stations unavailable, falling back to wizard:",
        error,
      )
      return request(`${WIZARD}@${vhost}/wizard`)
    }
  },
  // The vhost's route codes (keys of the wizard's route_stop_order) for the
  // crew board's route picker; empty where the vhost has none.
  getRouteCodes: async (client: Client): Promise<string[]> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())
    const response = await measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: `${WIZARD}@${vhost}/wizard`,
        },
        xml("command", {
          node: "get_route_codes",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        }),
      ),
    )
    const command = response.getChild("command")
    const form = command && DataFormsParser.parse(command)
    return form?.fields?.["route_codes"]?.values ?? []
  },
  getAutohofs: async (client: Client): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: `${WIZARD}@${vhost}/wizard`,
        },
        xml("command", {
          node: "get_autohofs",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        }),
      ),
    )
  },
  getFuelReport: async (
    client: Client,
    dateStart: string,
    dateEnd: string,
    driverJid?: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const targetApi = `wizard@${vhost}/gps`

    const fields = [
      xml(
        "field",
        { var: "date_start", type: "text-single" },
        xml("value", {}, dateStart),
      ),
      xml(
        "field",
        { var: "date_end", type: "text-single" },
        xml("value", {}, dateEnd),
      ),
    ]
    if (driverJid) {
      fields.push(
        xml(
          "field",
          { var: "driver_jid", type: "text-single" },
          xml("value", {}, driverJid),
        ),
      )
    }

    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: targetApi },
        xml(
          "command",
          {
            node: "get_fuel_report",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  getFuelAlertDetail: async (
    client: Client,
    driverJid: string,
    eventId: string,
    opts: { lookaheadHours: number; radiusM: number; seenHorizonHours: number },
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const targetApi = `wizard@${vhost}/gps`

    const fields = [
      xml(
        "field",
        { var: "driver_jid", type: "text-single" },
        xml("value", {}, driverJid),
      ),
      xml(
        "field",
        { var: "event_id", type: "text-single" },
        xml("value", {}, eventId),
      ),
      xml(
        "field",
        { var: "lookahead_hours", type: "text-single" },
        xml("value", {}, String(opts.lookaheadHours)),
      ),
      xml(
        "field",
        { var: "radius_m", type: "text-single" },
        xml("value", {}, String(opts.radiusM)),
      ),
      xml(
        "field",
        { var: "seen_horizon_hours", type: "text-single" },
        xml("value", {}, String(opts.seenHorizonHours)),
      ),
    ]

    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: targetApi },
        xml(
          "command",
          {
            node: "get_fuel_alert_detail",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  getFuelTelemetry: async (
    client: Client,
    driverJid: string,
    tsFrom: string,
    tsTo: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const targetApi = `wizard@${vhost}/gps`

    const fields = [
      xml(
        "field",
        { var: "driver_jid", type: "text-single" },
        xml("value", {}, driverJid),
      ),
      xml(
        "field",
        { var: "timestamp_from", type: "text-single" },
        xml("value", {}, tsFrom),
      ),
      xml(
        "field",
        { var: "timestamp_to", type: "text-single" },
        xml("value", {}, tsTo),
      ),
    ]

    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: targetApi },
        xml(
          "command",
          {
            node: "get_fuel_telemetry",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },
  getRoutePolylines: async (
    client: Client,
    to: string,
    timestamp?: string,
    // Keep the full planned geometry instead of trimming the first leg to
    // the driver's position at `timestamp` (used by the fuel report map).
    fullRoute?: boolean,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(to)
    const targetApi = `wizard@${vhost}/gps`
    const fields = [
      xml(
        "field",
        { var: "driver_jid", type: "jid-single" },
        xml("value", {}, to),
      ),
    ]

    // Add timestamp field if timestamp is present
    if (timestamp) {
      fields.push(
        xml(
          "field",
          { var: "timestamp", type: "text-single" },
          xml("value", {}, timestamp),
        ),
      )
    }

    if (fullRoute) {
      fields.push(
        xml(
          "field",
          { var: "full_route", type: "text-single" },
          xml("value", {}, "1"),
        ),
      )
    }

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "get_route_polylines",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },
  getNewRoutePolylines: async (
    client: Client,
    to: string,
    waypoints: [number, number][],
    selectedAvoidOptions?: AvoidType[],
    selectedLoadType?: LoadType[],
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(to)
    const targetApi = `wizard@${vhost}/gps`
    console.debug(
      "Fetching order polylines with avoid:",
      selectedAvoidOptions,
      "and loadType:",
      selectedLoadType,
    )

    const commandChildren = [
      xml(
        "x",
        { xmlns: "jabber:x:data", type: "form" },
        xml(
          "field",
          { var: "driver_jid", type: "jid-single" },
          xml("value", {}, to),
        ),
        xml(
          "field",
          { var: "stops", type: "text-single" },
          xml("value", {}, waypoints.map((w) => `${w[1]},${w[0]}`).join(";")),
        ),
      ),
    ]
    if (selectedAvoidOptions && selectedLoadType) {
      commandChildren.unshift(
        SettingsParser.buildCarRouteConfigElement({
          avoid: selectedAvoidOptions,
          loadType: selectedLoadType,
        }),
      )
    }

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "get_adhoc_route_polylines",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          ...commandChildren,
        ),
      ),
    )
  },
  setDriverGPSDevice: async (
    client: Client,
    to: string,
    device: string | null,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(to)
    const targetApi = `wizard@${vhost}/gps`

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "set_gps_device",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "submit" },
            device !== null
              ? xml("field", { var: "device_id" }, xml("value", {}, device))
              : xml("field", { var: "device_id" }, xml("value", {})),
            xml("field", { var: "jid" }, xml("value", {}, to)),
          ),
        ),
      ),
    )
  },

  getRooms: async (client: Client): Promise<GetRoomsItem[]> => {
    const [accountChats, userChats] = await Promise.all([
      XmppPubSubApi.fetchAccountChats(client),
      XmppPubSubApi.fetchUserChats(client),
    ])

    const chatsByJid = new Map<string, (typeof accountChats)[number]>()
    userChats.forEach((chat) => chatsByJid.set(chat.jid, chat))
    accountChats.forEach((chat) => chatsByJid.set(chat.jid, chat))

    return Array.from(chatsByJid.values()).map((accountChat) => {
      return {
        jid: accountChat.jid,
        name: accountChat.name,
        groups: accountChat.groupDescriptions,
      }
    })
  },

  shareFile: async ({
    client,
    fileUrl,
    userJid,
    messageId,
    messageRoomJid,
    triggeringRoomJid,
  }: {
    client: Client
    fileUrl: string
    userJid: string
    messageId: string
    messageRoomJid: string | undefined
    triggeringRoomJid: string
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: `wizard@${vhost}/wizard`,
        },
        xml(
          "command",
          {
            node: "share_file",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "file_url", type: "text-single" },
              xml("value", {}, fileUrl),
            ),
            xml(
              "field",
              { var: "message_id", type: "text-single" },
              xml("value", {}, messageId),
            ),
            xml(
              "field",
              { var: "user_jid", type: "text-single" },
              xml("value", {}, userJid),
            ),
            xml(
              "field",
              { var: "message_room_jid", type: "text-single" },
              xml("value", {}, messageRoomJid || ""),
            ),
            xml(
              "field",
              { var: "triggering_room_jid", type: "text-single" },
              xml("value", {}, triggeringRoomJid),
            ),
          ),
        ),
      ),
    )
  },
  search: async ({
    client,
    query,
    chatJid,
    page = 0,
    since,
    until,
  }: {
    client: Client
    query: string
    chatJid: string
    page?: number
    since?: Date
    until?: Date
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())

    const formFields = [
      xml(
        "field",
        { var: "query", type: "text-single" },
        xml("value", {}, query),
      ),
      xml(
        "field",
        { var: "chat_jid", type: "text-single" },
        xml("value", {}, chatJid),
      ),
      xml(
        "field",
        { var: "page", type: "text-single" },
        xml("value", {}, page.toString()),
      ),
    ]

    if (since) {
      formFields.push(
        xml(
          "field",
          { var: "since", type: "text-single" },
          xml("value", {}, since.toISOString()),
        ),
      )
    }

    if (until) {
      formFields.push(
        xml(
          "field",
          { var: "until", type: "text-single" },
          xml("value", {}, until.toISOString()),
        ),
      )
    }

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: `wizard@${vhost}/wizard`,
        },
        xml(
          "command",
          {
            node: "search",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...formFields),
        ),
      ),
    )
  },
  searchStreamed: async ({
    client,
    query,
    chatJid,
    page = 0,
    since,
    until,
  }: {
    client: Client
    query: string
    chatJid: string
    page?: number
    since?: Date
    until?: Date
  }): Promise<{ hits: SearchHit[]; found: number; sent: number }> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller, "command.search_streamed")
    const vhost = extractVhostFromJid(client!.jid!.toString())

    const queryid = crypto.randomUUID()

    const formFields = [
      xml(
        "field",
        { var: "query", type: "text-single" },
        xml("value", {}, query),
      ),
      xml(
        "field",
        { var: "chat_jid", type: "text-single" },
        xml("value", {}, chatJid),
      ),
      xml(
        "field",
        { var: "page", type: "text-single" },
        xml("value", {}, page.toString()),
      ),
      xml(
        "field",
        { var: "queryid", type: "text-single" },
        xml("value", {}, queryid),
      ),
    ]

    if (since) {
      formFields.push(
        xml(
          "field",
          { var: "since", type: "text-single" },
          xml("value", {}, since.toISOString()),
        ),
      )
    }

    if (until) {
      formFields.push(
        xml(
          "field",
          { var: "until", type: "text-single" },
          xml("value", {}, until.toISOString()),
        ),
      )
    }

    startCollecting(queryid)

    let response
    try {
      response = await measuredIqCaller.request(
        xml(
          "iq",
          {
            from: client.jid,
            type: "set",
            to: `wizard@${vhost}/wizard`,
          },
          xml(
            "command",
            {
              node: "search_streamed",
              xmlns: "http://jabber.org/protocol/commands",
            },
            xml("x", { xmlns: "jabber:x:data", type: "form" }, ...formFields),
          ),
        ),
      )
    } catch (err) {
      discardQuery(queryid)
      throw err
    }

    // Parse the metadata from the ad-hoc IQ response
    const commandEl = response.getChild("command")
    const jsonEl = commandEl?.getChild("json", "urn:xmpp:json:0")
    let found = 0
    let sent = 0
    if (jsonEl) {
      try {
        const meta = JSON.parse(jsonEl.getText())
        found = meta.found ?? 0
        sent = meta.sent ?? 0
      } catch {
        // ignore parse errors
      }
    }

    // Collect the streamed hits (guaranteed to have arrived before the IQ)
    const hits = collectResults(queryid)

    return { hits, found, sent }
  },
  getMessageContextFromSearch: async ({
    client,
    timestamp,
    chatJid,
  }: {
    client: Client
    timestamp: string
    chatJid: string
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: `wizard@${vhost}/wizard`,
        },
        xml(
          "command",
          {
            node: "get_message_context_from_search",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "timestamp", type: "text-single" },
              xml("value", {}, timestamp),
            ),
            xml(
              "field",
              { var: "chat_jid", type: "text-single" },
              xml("value", {}, chatJid),
            ),
          ),
        ),
      ),
    )
  },

  translateText: async ({
    client,
    text,
    targetLanguage,
    sourceLanguage,
  }: {
    client: Client
    text: string
    targetLanguage: string
    sourceLanguage?: string
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: `wizard@${vhost}/wizard`,
        },
        xml(
          "command",
          {
            node: TRANSLATE_TEXT_COMMAND_NODE,
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "text", type: "text-single" },
              xml("value", {}, text),
            ),
            xml(
              "field",
              { var: "target_language", type: "text-single" },
              xml("value", {}, targetLanguage),
            ),
            ...(sourceLanguage
              ? [
                  xml(
                    "field",
                    { var: "source_language", type: "text-single" },
                    xml("value", {}, sourceLanguage),
                  ),
                ]
              : []),
          ),
        ),
      ),
      TRANSLATE_TEXT_IQ_TIMEOUT_MS,
    )
  },

  updateWhatsappStatus: ({
    client,
    status,
  }: {
    client: Client
    status: string
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `${WIZARD}@${client.jid!.domain}/wizard`,
      },
      xml(
        "command",
        {
          node: "update_whatsapp_status",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml(
          "x",
          { xmlns: "jabber:x:data", type: "form" },
          xml(
            "field",
            { var: "status", type: "text-single" },
            xml("value", {}, status),
          ),
        ),
      ),
    )
    return measuredIqCaller.request(stanza)
  },

  linkDocument: ({
    client,
    cmrMessageId,
    deliveryOrderMessageId,
    cmrFileUrl,
    cmrFileName,
    deliveryOrderFileUrl,
    deliveryOrderFileName,
    messageRoomJid,
    triggeringRoomJid,
  }: {
    client: Client
    cmrMessageId: string
    deliveryOrderMessageId: string
    cmrFileUrl: string
    cmrFileName: string
    deliveryOrderFileUrl: string
    deliveryOrderFileName: string
    messageRoomJid: string
    triggeringRoomJid: string
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(triggeringRoomJid)
    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `${WIZARD}@${vhost}/wizard`,
      },
      xml(
        "command",
        {
          node: "link_document",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml(
          "x",
          { xmlns: "jabber:x:data", type: "form" },
          xml(
            "field",
            { var: "cmr_message_id", type: "text-single" },
            xml("value", {}, cmrMessageId),
          ),
          xml(
            "field",
            { var: "delivery_order_message_id", type: "text-single" },
            xml("value", {}, deliveryOrderMessageId),
          ),
          xml(
            "field",
            { var: "message_room_jid", type: "jid-single" },
            xml("value", {}, messageRoomJid || ""),
          ),
          xml(
            "field",
            { var: "triggering_room_jid", type: "jid-single" },
            xml("value", {}, triggeringRoomJid),
          ),
          xml(
            "field",
            { var: "cmr_file_url", type: "text-single" },
            xml("value", {}, cmrFileUrl),
          ),
          xml(
            "field",
            { var: "delivery_order_file_url", type: "text-single" },
            xml("value", {}, deliveryOrderFileUrl),
          ),
          xml(
            "field",
            { var: "cmr_file_name", type: "text-single" },
            xml("value", {}, cmrFileName),
          ),
          xml(
            "field",
            { var: "delivery_order_file_name", type: "text-single" },
            xml("value", {}, deliveryOrderFileName),
          ),
        ),
      ),
    )
    return measuredIqCaller.request(stanza)
  },

  addExternalContact: ({
    client,
    phoneNumber,
    firstName,
    lastName,
  }: {
    client: Client
    phoneNumber: string
    firstName: string
    lastName: string
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `communicator@${vhost}/forwarder`,
      },
      xml(
        "command",
        {
          node: "add_external_contact",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml(
          "x",
          { xmlns: "jabber:x:data", type: "form" },
          xml(
            "field",
            { var: "phone_number", type: "text-single" },
            xml("value", {}, phoneNumber),
          ),
          xml(
            "field",
            { var: "first_name", type: "text-single" },
            xml("value", {}, firstName),
          ),
          xml(
            "field",
            { var: "last_name", type: "text-single" },
            xml("value", {}, lastName),
          ),
        ),
      ),
    )
    return measuredIqCaller.request(stanza)
  },

  addDriver: ({
    client,
    phoneNumber,
    firstName,
    lastName,
    plates,
    userJID,
    skipMuc,
  }: {
    client: Client
    phoneNumber: string
    firstName: string
    lastName: string
    plates?: string
    userJID?: string
    skipMuc?: boolean
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `wizard@${vhost}/account_config`,
      },
      xml(
        "command",
        {
          node: "upsert_user",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml(
          "x",
          { xmlns: "jabber:x:data", type: "form" },
          xml(
            "field",
            { var: "first_name", type: "text-single" },
            xml("value", {}, firstName),
          ),
          xml(
            "field",
            { var: "last_name", type: "text-single" },
            xml("value", {}, lastName),
          ),
          xml(
            "field",
            { var: "phone_number", type: "text-single" },
            xml("value", {}, phoneNumber),
          ),
          ...(plates
            ? [
                xml(
                  "field",
                  { var: "plates", type: "text-single" },
                  xml("value", {}, plates),
                ),
              ]
            : []),
          ...(userJID
            ? [
                xml(
                  "field",
                  { var: "user_jid", type: "text-single" },
                  xml("value", {}, userJID),
                ),
              ]
            : []),
          ...(skipMuc
            ? [
                xml(
                  "field",
                  { var: "skip_muc", type: "text-single" },
                  xml("value", {}, "1"),
                ),
              ]
            : []),
        ),
      ),
    )
    return measuredIqCaller.request(stanza)
  },
  shareDriver: (
    client: Client,
    driverJid: string,
    share: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `wizard@${vhost}/account_config`,
      },
      xml(
        "command",
        {
          node: "share_driver",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml(
          "x",
          { xmlns: "jabber:x:data", type: "form" },
          xml(
            "field",
            { var: "jid", type: "text-single" },
            xml("value", {}, driverJid),
          ),
          xml(
            "field",
            { var: "share_to", type: "text-single" },
            xml("value", {}, share),
          ),
        ),
      ),
    )
    return measuredIqCaller.request(stanza)
  },
  getAvailableDriverShares: ({
    client,
  }: {
    client: Client
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `wizard@${vhost}/account_config`,
      },
      xml("command", {
        node: "get_available_driver_shares",
        action: "execute",
        xmlns: "http://jabber.org/protocol/commands",
      }),
    )
    return measuredIqCaller.request(stanza)
  },
  getCurrentShareDriver: ({
    client,
    driverJid,
  }: {
    client: Client
    driverJid: string
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `wizard@${vhost}/account_config`,
      },
      xml(
        "command",
        {
          node: "get_current_share_driver",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml(
          "x",
          { xmlns: "jabber:x:data", type: "form" },
          xml(
            "field",
            { var: "jid", type: "text-single" },
            xml("value", {}, driverJid),
          ),
        ),
      ),
    )
    return measuredIqCaller.request(stanza)
  },

  updateCarConfig: (
    client: Client,
    driverJid: string,
    config: Partial<CarRouteConfig>,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())

    // Create the form with driver JID
    const formElements = [
      xml(
        "field",
        { var: "driver_jid", type: "jid-single", label: "Driver JID" },
        xml("value", {}, driverJid),
      ),
    ]

    formElements.push(
      xml(
        "field",
        { var: "width", type: "text-single", label: "Width" },
        xml("value", {}, config.width || ""),
      ),
    )

    formElements.push(
      xml(
        "field",
        { var: "height", type: "text-single", label: "Height" },
        xml("value", {}, config.height || ""),
      ),
    )

    formElements.push(
      xml(
        "field",
        { var: "length", type: "text-single", label: "Length" },
        xml("value", {}, config.length || ""),
      ),
    )

    formElements.push(
      xml(
        "field",
        { var: "weight", type: "text-single", label: "Weight" },
        xml("value", {}, config.weight || ""),
      ),
    )

    formElements.push(
      xml(
        "field",
        { var: "max-speed", type: "text-single", label: "Max speed" },
        xml("value", {}, config.maxSpeed || ""),
      ),
    )

    formElements.push(
      xml(
        "field",
        { var: "travel-mode", type: "text-single", label: "Travel mode" },
        xml("value", {}, config.travelMode || ""),
      ),
    )

    formElements.push(
      xml(
        "field",
        { var: "emission-type", type: "text-single", label: "Emission Type" },
        xml("value", {}, config.emissionType || ""),
      ),
    )

    formElements.push(
      xml(
        "field",
        { var: "axle-count", type: "text-single", label: "Axle Count" },
        xml("value", {}, config.axleCount || ""),
      ),
    )

    formElements.push(
      xml(
        "field",
        { var: "co2-class", type: "text-single", label: "CO2 Class" },
        xml("value", {}, config.co2class || ""),
      ),
    )

    formElements.push(
      xml(
        "field",
        {
          var: "fuel-consumption",
          type: "text-single",
          label: "Fuel consumption (l/100km)",
        },
        xml("value", {}, config.fuelConsumption || ""),
      ),
    )

    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `wizard@${vhost}/gps`,
      },
      xml(
        "command",
        {
          node: "upsert_car_route_config",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml("x", { xmlns: "jabber:x:data", type: "form" }, ...formElements),
      ),
    )

    return measuredIqCaller.request(stanza)
  },
  updateRouteConfig: (
    client: Client,
    driverJid: string,
    config: Partial<CarRouteConfig>,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())

    // Create the form with driver JID
    const formElements = [
      xml(
        "field",
        { var: "driver_jid", type: "jid-single", label: "Driver JID" },
        xml("value", {}, driverJid),
      ),
    ]

    if (config.avoid) {
      const options = config.avoid.map((item) => ({
        value: item,
        label: item, // We could map this to a user-friendly label if needed
      }))

      formElements.push(
        xml(
          "field",
          { var: "avoid", type: "list-multi", label: "Avoid" },
          ...options.map((option) =>
            xml(
              "option",
              { label: option.label },
              xml("value", {}, option.value),
            ),
          ),
          ...options.map((option) => xml("value", {}, option.value)),
        ),
      )
    }

    if (config.loadType) {
      const options = config.loadType.map((item) => ({
        value: item,
        label: item,
      }))

      formElements.push(
        xml(
          "field",
          { var: "load-type", type: "list-multi", label: "Load Type" },
          ...options.map((option) =>
            xml(
              "option",
              { label: option.label },
              xml("value", {}, option.value),
            ),
          ),
          ...options.map((option) => xml("value", {}, option.value)),
        ),
      )
    }

    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `wizard@${vhost}/gps`,
      },
      xml(
        "command",
        {
          node: "upsert_car_route_config",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml("x", { xmlns: "jabber:x:data", type: "form" }, ...formElements),
      ),
    )

    return measuredIqCaller.request(stanza)
  },
  updateRouteAlertConfig: (
    client: Client,
    driverJid: string,
    config: Partial<RouteAlertConfig>,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())

    // Create the form with driver JID
    const formElements = [
      xml(
        "field",
        { var: "driver_jid", type: "jid-single", label: "Driver JID" },
        xml("value", {}, driverJid),
      ),
    ]

    // Add temperature alert fields
    ;[
      "temperature_main_alert_level_min",
      "temperature_main_alert_level_max",
      "temperature_additional_alert_level_min",
      "temperature_additional_alert_level_max",
    ].forEach((field) => {
      const v = config[field as keyof RouteAlertConfig]
      if (v !== undefined) {
        formElements.push(
          xml(
            "field",
            {
              var: field.replace(/_/g, "-"),
              type: "text-single",
              label: field.replace(/_/g, "-"),
            },
            xml("value", {}, v.toString()),
          ),
        )
      }
    })

    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `wizard@${vhost}/gps`,
      },
      xml(
        "command",
        {
          node: "upsert_route_alert_config",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml("x", { xmlns: "jabber:x:data", type: "form" }, ...formElements),
      ),
    )

    return measuredIqCaller.request(stanza)
  },
  upsertReferenceRoute: (
    client: Client,
    driverJid: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(driverJid)
    const targetApi = `wizard@${vhost}/gps`

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "upsert_reference_route",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "driver_jid", type: "jid-single" },
              xml("value", {}, driverJid),
            ),
          ),
        ),
      ),
    )
  },

  // Read reference route for driver
  readReferenceRoute: async (
    client: Client,
    driverJid: string,
    alertId?: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(driverJid)
    const targetApi = `wizard@${vhost}/gps`

    const fields = [
      xml(
        "field",
        { var: "driver_jid", type: "jid-single" },
        xml("value", {}, driverJid),
      ),
    ]

    // Add alert_id field if alertId is present
    if (alertId) {
      fields.push(
        xml(
          "field",
          { var: "alert_id", type: "text-single" },
          xml("value", {}, alertId),
        ),
      )
    }

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "get_reference_route",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  getGboxTimelineView: async (
    client: Client,
    chatJids: string[] = [],
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())
    const uniqueChatJids = Array.from(
      new Set(chatJids.map((chatJid) => chatJid.trim()).filter(Boolean)),
    )

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: `${WIZARD}@${vhost}/gps`,
        },
        xml(
          "command",
          {
            node: GBOX_TIMELINE_COMMAND_NODE,
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "chat_jids", type: "jid-multi" },
              ...uniqueChatJids.map((chatJid) => xml("value", {}, chatJid)),
            ),
          ),
        ),
      ),
      TIMELINE_VIEW_IQ_TIMEOUT_MS,
    )
  },

  // gps pages results (see navigator_timeline_query.py) so a single IQ
  // stanza never exceeds ejabberd's max_stanza_size — a full account's
  // worth of feeds (e.g. 105+ vehicles) is large enough to get the c2s
  // connection dropped mid-write when sent as one stanza. Follow
  // next_after_chat_jid until has_more is false, merging every page's
  // <navigator-timeline> children into one synthetic <command> element so
  // NavigatorTimelineParser.parse (and its callers) stay unchanged.
  getNavigatorTimelineView: async (client: Client): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())
    const mergedCommand = xml("command", {
      xmlns: "http://jabber.org/protocol/commands",
    })
    let afterChatJid: string | null = null
    let failedAccounts: string | null = null

    while (true) {
      const fields: Element[] = afterChatJid
        ? [
            xml(
              "field",
              { var: "after_chat_jid", type: "text-single" },
              xml("value", {}, afterChatJid),
            ),
          ]
        : []
      const reply: Element = await measuredIqCaller.request(
        xml(
          "iq",
          {
            from: client.jid,
            type: "set",
            to: `${WIZARD}@${vhost}/gps`,
          },
          xml(
            "command",
            {
              node: NAVIGATOR_TIMELINE_COMMAND_NODE,
              action: "execute",
              xmlns: "http://jabber.org/protocol/commands",
            },
            ...(fields.length > 0
              ? [xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields)]
              : []),
          ),
        ),
        TIMELINE_VIEW_IQ_TIMEOUT_MS,
      )

      const commandElement: Element | undefined = reply.getChild(
        "command",
        "http://jabber.org/protocol/commands",
      )
      for (const feedElement of commandElement?.getChildren(
        "navigator-timeline",
        NAVIGATOR_TIMELINE_NS,
      ) ?? []) {
        mergedCommand.append(feedElement)
      }

      const pageMarker: Element | undefined = commandElement?.getChild(
        NAVIGATOR_TIMELINE_PAGE_ELEMENT,
        NAVIGATOR_TIMELINE_NS,
      )
      const hasMore = pageMarker?.getAttr("has_more") === "1"
      const nextAfterChatJid: string | null =
        pageMarker?.getAttr("next_after_chat_jid") || null
      const rawFailedAccounts: string | undefined =
        pageMarker?.getAttr("failed_accounts")
      if (rawFailedAccounts !== undefined) failedAccounts = rawFailedAccounts
      if (!hasMore || !nextAfterChatJid) break
      afterChatJid = nextAfterChatJid
    }

    // Merging drops the per-page markers, so re-attach the view-level failure
    // count — readNavigatorTimelineFailedAccounts reads it off the result to
    // tell an unreachable Navigator from a fleet that simply has no stops.
    if (failedAccounts !== null) {
      mergedCommand.append(
        xml(NAVIGATOR_TIMELINE_PAGE_ELEMENT, {
          xmlns: NAVIGATOR_TIMELINE_NS,
          failed_accounts: failedAccounts,
        }),
      )
    }

    return xml("iq", {}, mergedCommand)
  },

  getNavigatorVehicleDetails: async (
    client: Client,
    entityJid: string,
    vehicleId?: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())
    const fields = [
      xml(
        "field",
        { var: "entity_jid", type: "jid-single" },
        xml("value", {}, entityJid),
      ),
    ]
    if (vehicleId) {
      fields.push(
        xml(
          "field",
          { var: "vehicle_id", type: "text-single" },
          xml("value", {}, vehicleId),
        ),
      )
    }

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: `${WIZARD}@${vhost}/gps`,
        },
        xml(
          "command",
          {
            node: NAVIGATOR_VEHICLE_DETAILS_COMMAND_NODE,
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  getVehicleApproachDistances: async (
    client: Client,
    destination: { lat: number; lon: number },
    origins: { chatJid: string; lat: number; lon: number }[],
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())
    const fields = [
      xml(
        "field",
        { var: "destination", type: "text-single" },
        xml("value", {}, `${destination.lat},${destination.lon}`),
      ),
      xml(
        "field",
        { var: "origins", type: "text-single" },
        xml("value", {}, JSON.stringify(origins)),
      ),
    ]

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: `${WIZARD}@${vhost}/gps`,
        },
        xml(
          "command",
          {
            node: VEHICLE_APPROACH_COMMAND_NODE,
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  getNavigatorAttachment: async (
    client: Client,
    attachmentId: string,
    fileName?: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())
    const fields = [
      xml(
        "field",
        { var: "attachment_id", type: "text-single" },
        xml("value", {}, attachmentId),
      ),
    ]
    if (fileName) {
      fields.push(
        xml(
          "field",
          { var: "file_name", type: "text-single" },
          xml("value", {}, fileName),
        ),
      )
    }

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: `${WIZARD}@${vhost}/gps`,
        },
        xml(
          "command",
          {
            node: NAVIGATOR_ATTACHMENT_COMMAND_NODE,
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  getPlatformScienceTimelineView: async (
    client: Client,
    chatJids: string[] = [],
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())
    const uniqueChatJids = Array.from(
      new Set(chatJids.map((chatJid) => chatJid.trim()).filter(Boolean)),
    )

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: `${WIZARD}@${vhost}/gps`,
        },
        xml(
          "command",
          {
            node: PLATFORM_SCIENCE_TIMELINE_COMMAND_NODE,
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "chat_jids", type: "jid-multi" },
              ...uniqueChatJids.map((chatJid) => xml("value", {}, chatJid)),
            ),
          ),
        ),
      ),
      TIMELINE_VIEW_IQ_TIMEOUT_MS,
    )
  },

  readDriverPolyline: async (
    client: Client,
    driverJid: string,
    alertId?: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(driverJid)
    const targetApi = `wizard@${vhost}/gps`

    const fields = [
      xml(
        "field",
        { var: "driver_jid", type: "jid-single" },
        xml("value", {}, driverJid),
      ),
    ]

    // Add alert_id field if alertId is present
    if (alertId) {
      fields.push(
        xml(
          "field",
          { var: "alert_id", type: "text-single" },
          xml("value", {}, alertId),
        ),
      )
    }

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "get_driver_polyline_for_reference_route",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  // Clear reference route for driver
  clearReferenceRoute: (
    client: Client,
    driverJid: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(driverJid)
    const targetApi = `wizard@${vhost}/gps`

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "clear_reference_route",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "driver_jid", type: "jid-single" },
              xml("value", {}, driverJid),
            ),
          ),
        ),
      ),
    )
  },
  // Clear reference route for driver
  shareRoute: (client: Client, driverJid: string): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(driverJid)
    const targetApi = `wizard@${vhost}/gps`

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "share_route",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "driver_jid", type: "jid-single" },
              xml("value", {}, driverJid),
            ),
          ),
        ),
      ),
    )
  },
  getAvoidanceItems: async (
    client: Client,
    driverJid: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(driverJid)
    const targetApi = `wizard@${vhost}/gps`

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "get_avoidance_items",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "driver_jid", type: "jid-single" },
              xml("value", {}, driverJid),
            ),
          ),
        ),
      ),
    )
  },
  getRouteTextSummary: (
    client: Client,
    driverJid: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(driverJid)
    const targetApi = `wizard@${vhost}/gps`

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "get_route_text_summary",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "driver_jid", type: "jid-single" },
              xml("value", {}, driverJid),
            ),
          ),
        ),
      ),
    )
  },
  getRouteGoogleLink: (client: Client, driverJid: string): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(driverJid)
    const targetApi = `wizard@${vhost}/gps`

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "get_route_google_link",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "driver_jid", type: "jid-single" },
              xml("value", {}, driverJid),
            ),
          ),
        ),
      ),
    )
  },

  getRoutePlan: async (
    client: Client,
    to: string,
    stops: Route2g["stops"],
    carConfig: CarRouteConfig,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(to)
    const targetApi = `wizard@${vhost}/gps`

    const response = await measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "plan_route",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          SettingsParser.buildCarRouteConfigElement(carConfig),
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "reported",
              {},
              xml("field", { var: "address", type: "text-single" }),
              xml("field", { var: "address-updated", type: "boolean" }),
              xml("field", { var: "id", type: "text-single" }),
              xml("field", { var: "msg-id", type: "text-single" }),
              xml("field", { var: "action", type: "text-single" }),
              xml("field", {
                var: "action_reference_number",
                type: "text-single",
              }),
              xml("field", { var: "ref", type: "text-single" }),
              xml("field", {
                var: "scheduled_arrival_time",
                type: "text-single",
              }),
              xml("field", {
                var: "avoid",
                type: "text-single",
              }),
              xml("field", { var: "company_name", type: "text-single" }),
              xml("field", { var: "postal_code", type: "text-single" }),
            ),
            ...buildStops(stops, { sendRouteHandle: true, useLatLon: true }),
            xml(
              "field",
              { var: "driver_jid", type: "jid-single" },
              xml("value", {}, to),
            ),
          ),
        ),
      ),
      PLAN_ROUTE_IQ_TIMEOUT_MS,
    )

    return response
  },

  getOrderPolylines: async (
    client: Client,
    to: string,
    stops: Route2g["stops"],
    carConfig: CarRouteConfig,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(to)
    const targetApi = `wizard@${vhost}/gps`

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "get_adhoc_route_with_directions",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          SettingsParser.buildCarRouteConfigElement(carConfig),
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "reported",
              {},
              xml("field", { var: "address", type: "text-single" }),
              xml("field", { var: "address-updated", type: "boolean" }),
              xml("field", { var: "id", type: "text-single" }),
              xml("field", { var: "msg-id", type: "text-single" }),
              xml("field", { var: "action", type: "text-single" }),
              xml("field", {
                var: "action_reference_number",
                type: "text-single",
              }),
              xml("field", { var: "ref", type: "text-single" }),
              xml("field", {
                var: "scheduled_arrival_time",
                type: "text-single",
              }),
              xml("field", {
                var: "avoid",
                type: "text-single",
              }),
              xml("field", { var: "company_name", type: "text-single" }),
              xml("field", { var: "postal_code", type: "text-single" }),
            ),
            ...buildStops(stops, { useLatLon: true }),
            xml(
              "field",
              { var: "driver_jid", type: "jid-single" },
              xml("value", {}, to),
            ),
          ),
        ),
      ),
    )
  },
  getTolls: async (client: Client, driverJid: string): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(driverJid)
    const targetApi = `wizard@${vhost}/gps`

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "get_tolls",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "driver_jid", type: "jid-single" },
              xml("value", {}, driverJid),
            ),
          ),
        ),
      ),
    )
  },

  extractRouteFromOrders: async (
    client: Client,
    driverJid: string,
    files: UploadedFile[],
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(driverJid)
    const targetApi = `assistant@${vhost}/assistant`

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "extract_route",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "driver_jid", type: "jid-single" },
              xml("value", {}, driverJid),
            ),
          ),
          ...files.map(({ url, file }) =>
            xml(
              "file-sharing",
              {
                xmlns: "urn:xmpp:sfs:0",
                disposition: "inline",
              },
              xml(
                "sources",
                {},
                xml("url-data", {
                  xmlns: "http://jabber.org/protocol/url-data",
                  target: url,
                }),
              ),
              xml(
                "file",
                "urn:xmpp:file:metadata:0",
                xml("media-type", {}, file!.type),
                xml("name", {}, file!.name),
                xml("size", {}, file!.size.toString()),
              ),
            ),
          ),
        ),
      ),
      120000,
    )
  },
  getDriverPolylines: async (
    client: Client,
    driverJid: string,
    since: string,
    until: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(driverJid)
    const targetApi = `wizard@${vhost}/gps`

    const fields = [
      xml(
        "field",
        { var: "driver_jid", type: "jid-single" },
        xml("value", {}, driverJid),
      ),
      xml(
        "field",
        { var: "since", type: "text-single" },
        xml("value", {}, since),
      ),
      xml(
        "field",
        { var: "until", type: "text-single" },
        xml("value", {}, until),
      ),
    ]

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "get_driver_polyline",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  getOrdersSummary: async (
    client: Client,
    chatJid: string,
    since: string,
    until: string,
    action:
      | "get_summary_and_orders"
      | "export_orders" = "get_summary_and_orders",
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const targetApi = `wizard@${vhost}/wizard`

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: targetApi,
        },
        xml(
          "command",
          {
            node: "get_orders_summary",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "chat_jid", type: "text-single" },
              xml("value", {}, chatJid),
            ),
            xml(
              "field",
              { var: "since", type: "text-single" },
              xml("value", {}, since),
            ),
            xml(
              "field",
              { var: "until", type: "text-single" },
              xml("value", {}, until),
            ),
            xml(
              "field",
              { var: "action", type: "text-single" },
              xml("value", {}, action),
            ),
          ),
        ),
      ),
    )
  },

  upsertCar: ({
    client,
    carId,
    name,
  }: {
    client: Client
    carId?: string
    name: string
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `wizard@${vhost}/account_config`,
      },
      xml(
        "command",
        {
          node: "upsert_car",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml(
          "x",
          { xmlns: "jabber:x:data", type: "form" },
          ...(carId
            ? [
                xml(
                  "field",
                  { var: "car_id", type: "text-single" },
                  xml("value", {}, carId),
                ),
              ]
            : []),
          xml(
            "field",
            { var: "name", type: "text-single" },
            xml("value", {}, name),
          ),
        ),
      ),
    )
    return measuredIqCaller.request(stanza)
  },

  createTransicsChat: ({
    client,
    carId,
    name,
  }: {
    client: Client
    carId: string
    name?: string
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `wizard@${vhost}/account_config`,
      },
      xml(
        "command",
        {
          node: "create_transics_chat",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml(
          "x",
          { xmlns: "jabber:x:data", type: "form" },
          xml(
            "field",
            { var: "car_id", type: "text-single" },
            xml("value", {}, carId),
          ),
          ...(name
            ? [
                xml(
                  "field",
                  { var: "name", type: "text-single" },
                  xml("value", {}, name),
                ),
              ]
            : []),
        ),
      ),
    )
    return measuredIqCaller.request(stanza)
  },

  createPlatformScienceChat: ({
    client,
    carId,
    name,
  }: {
    client: Client
    carId: string
    name?: string
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `wizard@${vhost}/account_config`,
      },
      xml(
        "command",
        {
          node: "create_platform_science_chat",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml(
          "x",
          { xmlns: "jabber:x:data", type: "form" },
          xml(
            "field",
            { var: "car_id", type: "text-single" },
            xml("value", {}, carId),
          ),
          ...(name
            ? [
                xml(
                  "field",
                  { var: "name", type: "text-single" },
                  xml("value", {}, name),
                ),
              ]
            : []),
        ),
      ),
    )
    return measuredIqCaller.request(stanza)
  },

  upsertMuc: ({
    client,
    mucJid,
    name,
    driverJid,
    carId,
    tmsCarId,
    tmsTrailerId,
    clearDriver,
  }: {
    client: Client
    mucJid?: string
    name: string
    // Left out, the chat keeps whoever sits in its first seat.
    driverJid?: string
    // Empties the first seat. An empty driverJid can't: account_config reads
    // it as "keep the driver".
    clearDriver?: boolean
    carId?: string
    tmsCarId?: string
    tmsTrailerId?: string
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `wizard@${vhost}/account_config`,
      },
      xml(
        "command",
        {
          node: "upsert_muc",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml(
          "x",
          { xmlns: "jabber:x:data", type: "form" },
          ...(mucJid
            ? [
                xml(
                  "field",
                  { var: "muc_jid", type: "jid-single" },
                  xml("value", {}, mucJid),
                ),
              ]
            : []),
          xml(
            "field",
            { var: "name", type: "text-single" },
            xml("value", {}, name),
          ),
          ...(driverJid !== undefined
            ? [
                xml(
                  "field",
                  { var: "driver_a", type: "jid-single" },
                  xml("value", {}, driverJid),
                ),
              ]
            : []),
          // An empty string is a value, not a missing one: it is how the form
          // says this chat has no car. Omitting the field instead would leave
          // whatever car the chat already holds.
          ...(carId !== undefined
            ? [
                xml(
                  "field",
                  { var: "car_id", type: "text-single" },
                  xml("value", {}, carId),
                ),
              ]
            : []),
          ...(tmsCarId !== undefined
            ? [
                xml(
                  "field",
                  { var: "tms_car_id", type: "text-single" },
                  xml("value", {}, tmsCarId),
                ),
              ]
            : []),
          ...(tmsTrailerId !== undefined
            ? [
                xml(
                  "field",
                  { var: "tms_trailer_id", type: "text-single" },
                  xml("value", {}, tmsTrailerId),
                ),
              ]
            : []),
          ...(clearDriver
            ? [
                xml(
                  "field",
                  { var: "clear_driver_a", type: "boolean" },
                  xml("value", {}, "1"),
                ),
              ]
            : []),
        ),
      ),
    )
    return measuredIqCaller.request(stanza)
  },

  getTmsCars: async (client: Client): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: `${WIZARD}@${vhost}/gps`,
        },
        xml("command", {
          node: TMS_CARS_COMMAND_NODE,
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        }),
      ),
    )
  },

  getTmsTrailers: async (client: Client): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())

    return measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: `${WIZARD}@${vhost}/gps`,
        },
        xml("command", {
          node: TMS_TRAILERS_COMMAND_NODE,
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        }),
      ),
    )
  },

  shareMuc: (
    client: Client,
    mucJid: string,
    shareTo: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `wizard@${vhost}/account_config`,
      },
      xml(
        "command",
        {
          node: "share_muc",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml(
          "x",
          { xmlns: "jabber:x:data", type: "form" },
          xml(
            "field",
            { var: "muc_jid", type: "jid-single" },
            xml("value", {}, mucJid),
          ),
          xml(
            "field",
            { var: "share_to", type: "text-single" },
            xml("value", {}, shareTo),
          ),
        ),
      ),
    )
    return measuredIqCaller.request(stanza)
  },

  getRosterSyncStatus: async (client: Client): Promise<RosterSyncStatus> => {
    const values = await accountConfigComponentCommand(
      client,
      ROSTER_SYNC_STATUS_COMMAND_NODE,
    )
    return {
      allowed: values.allowed === "true",
      readyIn: Number(values.readyIn ?? 0),
    }
  },

  runRosterSync: async (client: Client): Promise<RosterSyncRunResult> => {
    const values = await accountConfigComponentCommand(
      client,
      RUN_ROSTER_SYNC_COMMAND_NODE,
    )
    return {
      status: (values.status ?? "failed") as RosterSyncRunStatus,
      readyIn: Number(values.readyIn ?? 0),
      reason: values.reason ?? "",
    }
  },

  getAccountConfig: async (client: Client, key: string): Promise<unknown> => {
    const values = await accountConfigComponentCommand(
      client,
      GET_ACCOUNT_CONFIG_COMMAND_NODE,
      { key },
    )
    // The backend always sends json.dumps(stored), so "null" is the only
    // encoding of a missing key. No value field is an anomaly: callers must
    // not read it as an empty config and save over real data.
    if (!Object.prototype.hasOwnProperty.call(values, "value")) {
      throw new Error("account_config reply without value")
    }
    return JSON.parse(values.value) as unknown
  },

  setAccountConfig: async (
    client: Client,
    key: string,
    value: unknown,
  ): Promise<void> => {
    await accountConfigComponentCommand(
      client,
      SET_ACCOUNT_CONFIG_COMMAND_NODE,
      {
        key,
        value: JSON.stringify(value),
      },
    )
  },

  getDispatcherChats: (client: Client): Promise<Record<string, string[]>> =>
    dispatcherChatsCommand(client),

  setPreferredLanguage: ({
    client,
    driverJid,
    language,
  }: {
    client: Client
    driverJid: string
    language: string
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `wizard@${vhost}/account_config`,
      },
      xml(
        "command",
        {
          node: "set_preferred_language",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml(
          "x",
          { xmlns: "jabber:x:data", type: "form" },
          xml(
            "field",
            { var: "driver_jid", type: "jid-single" },
            xml("value", {}, driverJid),
          ),
          xml(
            "field",
            { var: "language", type: "text-single" },
            xml("value", {}, language),
          ),
        ),
      ),
    )
    return measuredIqCaller.request(stanza)
  },

  getCurrentShareMuc: ({
    client,
    mucJid,
  }: {
    client: Client
    mucJid: string
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const stanza = xml(
      "iq",
      {
        from: client.jid,
        type: "set",
        to: `wizard@${vhost}/account_config`,
      },
      xml(
        "command",
        {
          node: "get_current_share_muc",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        },
        xml(
          "x",
          { xmlns: "jabber:x:data", type: "form" },
          xml(
            "field",
            { var: "muc_jid", type: "jid-single" },
            xml("value", {}, mucJid),
          ),
        ),
      ),
    )
    return measuredIqCaller.request(stanza)
  },

  ensureAnonymousTranschat: async (
    client: Client,
    userId: string,
    name?: string,
  ): Promise<string> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())

    const fields = [
      xml(
        "field",
        { var: "user_id", type: "text-single" },
        xml("value", {}, userId),
      ),
    ]
    if (name) {
      fields.push(
        xml(
          "field",
          { var: "name", type: "text-single" },
          xml("value", {}, name),
        ),
      )
    }

    const result = await measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: `wizard@${vhost}/account_config`,
        },
        xml(
          "command",
          {
            node: "ensure_anonymous_transchat",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )

    const field = result.getChild("command")?.getChild("x")?.getChild("field")
    if (!field || field.getAttr("var") !== "room_jid") {
      throw new Error("Missing room_jid in ensure_anonymous_transchat response")
    }
    return field.getChild("value")?.getText() || ""
  },

  renameAnonymousTranschat: async (
    client: Client,
    userId: string,
    name: string,
  ): Promise<string> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())

    const result = await measuredIqCaller.request(
      xml(
        "iq",
        {
          from: client.jid,
          type: "set",
          to: `wizard@${vhost}/account_config`,
        },
        xml(
          "command",
          {
            node: "rename_anonymous_transchat",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "user_id", type: "text-single" },
              xml("value", {}, userId),
            ),
            xml(
              "field",
              { var: "name", type: "text-single" },
              xml("value", {}, name),
            ),
          ),
        ),
      ),
    )

    const field = result.getChild("command")?.getChild("x")?.getChild("field")
    if (!field || field.getAttr("var") !== "room_jid") {
      throw new Error("Missing room_jid in rename_anonymous_transchat response")
    }
    return field.getChild("value")?.getText() || ""
  },

  getCmrReport: async (
    client: Client,
    dateStart: string,
    dateEnd: string,
    reportDate: string,
    chatJid?: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const targetApi = `${WIZARD}@${vhost}/${WIZARD}`

    const fields = [
      xml(
        "field",
        { var: "date_start", type: "text-single" },
        xml("value", {}, dateStart),
      ),
      xml(
        "field",
        { var: "date_end", type: "text-single" },
        xml("value", {}, dateEnd),
      ),
      xml(
        "field",
        { var: "report_date", type: "text-single" },
        xml("value", {}, reportDate),
      ),
    ]
    if (chatJid) {
      fields.push(
        xml(
          "field",
          { var: "chat_jid", type: "text-single" },
          xml("value", {}, chatJid),
        ),
      )
    }

    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: targetApi },
        xml(
          "command",
          {
            node: "get_cmr_report",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  getDocumentsByUser: async (
    client: Client,
    userJid: string,
    dateStart: string,
    dateEnd: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const targetApi = `${WIZARD}@${vhost}/${WIZARD}`

    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: targetApi },
        xml(
          "command",
          {
            node: "get_documents_by_user",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "user_jid", type: "text-single" },
              xml("value", {}, userJid),
            ),
            xml(
              "field",
              { var: "date_start", type: "text-single" },
              xml("value", {}, dateStart),
            ),
            xml(
              "field",
              { var: "date_end", type: "text-single" },
              xml("value", {}, dateEnd),
            ),
          ),
        ),
      ),
    )
  },

  getDocumentsByMuc: async (
    client: Client,
    mucJid: string,
    dateStart: string,
    dateEnd: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const targetApi = `${WIZARD}@${vhost}/${WIZARD}`

    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: targetApi },
        xml(
          "command",
          {
            node: "get_documents_by_muc",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "muc_jid", type: "text-single" },
              xml("value", {}, mucJid),
            ),
            xml(
              "field",
              { var: "date_start", type: "text-single" },
              xml("value", {}, dateStart),
            ),
            xml(
              "field",
              { var: "date_end", type: "text-single" },
              xml("value", {}, dateEnd),
            ),
          ),
        ),
      ),
    )
  },

  /** GPS document links for the given document messages of a room, read by wizard from the GPS DB. */
  getGpsDocumentLinks: async (
    client: Client,
    roomJid: string,
    messageIds: string[],
  ): Promise<Element> => {
    const measuredIqCaller = iqMeasure(client.iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())

    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: `${WIZARD}@${vhost}/${WIZARD}` },
        xml(
          "command",
          {
            node: "get_gps_document_links",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            ...Object.entries({
              room_jid: roomJid,
              message_ids: JSON.stringify(messageIds),
            }).map(([name, value]) =>
              xml(
                "field",
                { var: name, type: "text-single" },
                xml("value", {}, value),
              ),
            ),
          ),
        ),
      ),
    )
  },

  /** Driver documents GPS matched to each delivery order, keyed by order ID. */
  getGpsOrderDocuments: async (
    client: Client,
    deliveryOrderIds: string[],
  ): Promise<Element> => {
    const measuredIqCaller = iqMeasure(client.iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())

    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: `${WIZARD}@${vhost}/${WIZARD}` },
        xml(
          "command",
          {
            node: "get_gps_order_documents",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "delivery_order_ids", type: "text-single" },
              xml("value", {}, JSON.stringify(deliveryOrderIds)),
            ),
          ),
        ),
      ),
    )
  },

  getAllDocuments: async (
    client: Client,
    dateStart: string,
    dateEnd: string,
    max?: number,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const targetApi = `${WIZARD}@${vhost}/${WIZARD}`

    const fields = [
      xml(
        "field",
        { var: "date_start", type: "text-single" },
        xml("value", {}, dateStart),
      ),
      xml(
        "field",
        { var: "date_end", type: "text-single" },
        xml("value", {}, dateEnd),
      ),
    ]
    if (max !== undefined) {
      fields.push(
        xml(
          "field",
          { var: "max", type: "text-single" },
          xml("value", {}, String(max)),
        ),
      )
    }

    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: targetApi },
        xml(
          "command",
          {
            node: "get_all_documents",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  getDocumentsByFileUrls: async (
    client: Client,
    fileUrls: string[],
    lookbackDays?: number,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const targetApi = `${WIZARD}@${vhost}/${WIZARD}`

    const fields = [
      xml(
        "field",
        { var: "file_urls", type: "text-multi" },
        ...fileUrls.map((url) => xml("value", {}, url)),
      ),
    ]
    if (lookbackDays !== undefined) {
      fields.push(
        xml(
          "field",
          { var: "lookback_days", type: "text-single" },
          xml("value", {}, String(lookbackDays)),
        ),
      )
    }

    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: targetApi },
        xml(
          "command",
          {
            node: "get_documents_by_file_urls",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  getDriverDeliveryOrders: async (
    client: Client,
    driverJid: string,
    limit: number = 5,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const targetApi = `${WIZARD}@${vhost}/${WIZARD}`

    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: targetApi },
        xml(
          "command",
          {
            node: "get_driver_delivery_orders",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "driver_jid", type: "jid-single" },
              xml("value", {}, driverJid),
            ),
            xml(
              "field",
              { var: "limit", type: "text-single" },
              xml("value", {}, String(limit)),
            ),
          ),
        ),
      ),
    )
  },

  cmrOrderAnnotation: async (
    client: Client,
    action: "get" | "set" | "remove",
    deliveryOrderMessageIds: string[],
    reportDate: string,
    chatJids?: Record<string, string>,
    note?: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const targetApi = `${WIZARD}@${vhost}/${WIZARD}`

    const fields = [
      xml(
        "field",
        { var: "action", type: "text-single" },
        xml("value", {}, action),
      ),
      xml(
        "field",
        { var: "delivery_order_message_ids", type: "text-single" },
        xml("value", {}, JSON.stringify(deliveryOrderMessageIds)),
      ),
      xml(
        "field",
        { var: "report_date", type: "text-single" },
        xml("value", {}, reportDate),
      ),
    ]
    if (chatJids) {
      fields.push(
        xml(
          "field",
          { var: "chat_jids", type: "text-single" },
          xml("value", {}, JSON.stringify(chatJids)),
        ),
      )
    }
    if (note) {
      fields.push(
        xml(
          "field",
          { var: "note", type: "text-single" },
          xml("value", {}, note),
        ),
      )
    }

    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: targetApi },
        xml(
          "command",
          {
            node: "cmr_order_annotation",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  cmrUnlinkedAnnotation: async (
    client: Client,
    action: "get" | "set" | "remove",
    groupKeys: string[],
    reportDate: string,
    chatJids?: Record<string, string>,
    note?: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const targetApi = `${WIZARD}@${vhost}/${WIZARD}`

    const fields = [
      xml(
        "field",
        { var: "action", type: "text-single" },
        xml("value", {}, action),
      ),
      xml(
        "field",
        { var: "group_keys", type: "text-single" },
        xml("value", {}, JSON.stringify(groupKeys)),
      ),
      xml(
        "field",
        { var: "report_date", type: "text-single" },
        xml("value", {}, reportDate),
      ),
    ]
    if (chatJids) {
      fields.push(
        xml(
          "field",
          { var: "chat_jids", type: "text-single" },
          xml("value", {}, JSON.stringify(chatJids)),
        ),
      )
    }
    if (note) {
      fields.push(
        xml(
          "field",
          { var: "note", type: "text-single" },
          xml("value", {}, note),
        ),
      )
    }

    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: targetApi },
        xml(
          "command",
          {
            node: "cmr_unlinked_annotation",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  getDeliveryOrders: async (client: Client): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const targetApi = `${WIZARD}@${vhost}/gps`

    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: targetApi },
        xml("command", {
          node: "get_delivery_orders",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        }),
      ),
    )
  },

  // Sends the get_prompt_suggestions XEP-0050 adhoc command to the assistant
  // (transchat@<vhost>) and parses the categorized JSON reply. Never throws —
  // returns an empty structure on any error so the FTUE degrades gracefully.
  getPromptSuggestions: async (
    client: Client,
    assistantJid: string,
  ): Promise<PromptSuggestions> => {
    try {
      const measuredIqCaller = iqMeasure(client.iqCaller)
      const reply = await measuredIqCaller.request(
        xml(
          "iq",
          { type: "set", to: assistantJid },
          xml("command", {
            node: "get_prompt_suggestions",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          }),
        ),
        30000,
      )
      const json = reply.getChild("command")?.getChildText("json")
      if (!json) return EMPTY_PROMPT_SUGGESTIONS
      const parsed = JSON.parse(json) as Partial<PromptSuggestions>
      return {
        popular: Array.isArray(parsed.popular) ? parsed.popular : [],
        groups: Array.isArray(parsed.groups) ? parsed.groups : [],
      }
    } catch (err) {
      console.warn("[promptSuggestions] failed to fetch suggestions", err)
      return EMPTY_PROMPT_SUGGESTIONS
    }
  },

  getNaviConfig: (client: Client, vhost: string): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    return measuredIqCaller.request(
      xml(
        "iq",
        { type: "set", to: `wizard@${vhost}/gps` },
        xml("command", {
          node: "get_navi_config",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        }),
      ),
    )
  },

  getNaviContractors: (
    client: Client,
    vhost: string,
    opts: { carrier?: boolean; query?: string; nip?: string; limit?: number },
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const fields = []
    if (opts.carrier !== undefined) {
      fields.push(
        xml(
          "field",
          { var: "carrier", type: "text-single" },
          xml("value", {}, String(opts.carrier)),
        ),
      )
    }
    if (opts.nip) {
      fields.push(
        xml(
          "field",
          { var: "nip", type: "text-single" },
          xml("value", {}, opts.nip),
        ),
      )
    }
    if (opts.query) {
      fields.push(
        xml(
          "field",
          { var: "query", type: "text-single" },
          xml("value", {}, opts.query),
        ),
      )
    }
    if (opts.limit) {
      fields.push(
        xml(
          "field",
          { var: "limit", type: "text-single" },
          xml("value", {}, String(opts.limit)),
        ),
      )
    }
    return measuredIqCaller.request(
      xml(
        "iq",
        { type: "set", to: `wizard@${vhost}/gps` },
        xml(
          "command",
          {
            node: "get_navi_dictionaries",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  getNaviDrivers: (
    client: Client,
    vhost: string,
    opts: { query?: string; phone?: string } = {},
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    // Drivers are never narrowed by carrier — any driver can serve any carrier,
    // so we show all of them. Send an explicit `drivers` flag so the backend
    // returns drivers (not contractors).
    const fields = [
      xml(
        "field",
        { var: "drivers", type: "text-single" },
        xml("value", {}, "1"),
      ),
    ]
    if (opts.query) {
      fields.push(
        xml(
          "field",
          { var: "query", type: "text-single" },
          xml("value", {}, opts.query),
        ),
      )
    }
    if (opts.phone) {
      fields.push(
        xml(
          "field",
          { var: "phone", type: "text-single" },
          xml("value", {}, opts.phone),
        ),
      )
    }
    return measuredIqCaller.request(
      xml(
        "iq",
        { type: "set", to: `wizard@${vhost}/gps` },
        xml(
          "command",
          {
            node: "get_navi_dictionaries",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  getNaviPersons: (
    client: Client,
    vhost: string,
    opts: { query?: string } = {},
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    // Office staff (created_by / forwarder refs) — non-driver Persons. Send an
    // explicit `persons` flag so the backend returns persons, not drivers.
    const fields = [
      xml(
        "field",
        { var: "persons", type: "text-single" },
        xml("value", {}, "1"),
      ),
    ]
    if (opts.query) {
      fields.push(
        xml(
          "field",
          { var: "query", type: "text-single" },
          xml("value", {}, opts.query),
        ),
      )
    }
    return measuredIqCaller.request(
      xml(
        "iq",
        { type: "set", to: `wizard@${vhost}/gps` },
        xml(
          "command",
          {
            node: "get_navi_dictionaries",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  getNaviVehicles: (
    client: Client,
    vhost: string,
    // Marcos Vehicle Type: "V" tractor (ciągnik, e.g. TRENAŻER), "T" trailer
    // (naczepa, e.g. TKI7410P). Both live in the same Vehicles collection.
    // Not filtered by carrier — any vehicle can serve any carrier.
    vehicleType: "V" | "T",
    opts: { query?: string } = {},
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const fields = [
      xml(
        "field",
        { var: "vehicles", type: "text-single" },
        xml("value", {}, vehicleType),
      ),
    ]
    if (opts.query) {
      fields.push(
        xml(
          "field",
          { var: "query", type: "text-single" },
          xml("value", {}, opts.query),
        ),
      )
    }
    return measuredIqCaller.request(
      xml(
        "iq",
        { type: "set", to: `wizard@${vhost}/gps` },
        xml(
          "command",
          {
            node: "get_navi_dictionaries",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields),
        ),
      ),
    )
  },

  createNaviOrder: (
    client: Client,
    vhost: string,
    driverJid: string,
    selection: NaviSelectionInput,
    stops?: Route2g["stops"],
    fileUrl?: string,
    fileName?: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    return measuredIqCaller.request(
      xml(
        "iq",
        { type: "set", to: `wizard@${vhost}/gps` },
        xml(
          "command",
          {
            node: "create_navi_order",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          naviSelectionForm(
            driverJid,
            selection,
            undefined,
            stops,
            fileUrl,
            fileName,
          ),
        ),
      ),
      CREATE_NAVI_ORDER_IQ_TIMEOUT_MS,
    )
  },

  // Asks gps for the coordinates plan_route will assign to these stops, so the
  // verify step can show them (and hand them to Marcos) before a route exists.
  // Same item fields as plan_route, so gps runs the identical geocoder call.
  geocodeStops: (
    client: Client,
    vhost: string,
    stops: GeocodeStopInput[],
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    return measuredIqCaller.request(
      xml(
        "iq",
        { type: "set", to: `${WIZARD}@${vhost}/gps` },
        xml(
          "command",
          {
            node: "geocode_stops",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "reported",
              {},
              xml("field", { var: "id", type: "text-single" }),
              xml("field", { var: "address", type: "text-single" }),
              xml("field", { var: "company_name", type: "text-single" }),
              xml("field", { var: "postal_code", type: "text-single" }),
            ),
            ...stops.map((stop) =>
              xml(
                "item",
                {},
                naviField("id", stop.id),
                naviField("address", stop.address),
                naviField("company_name", stop.companyName),
                naviField("postal_code", stop.postalCode),
              ),
            ),
          ),
        ),
      ),
    )
  },

  finalizeNaviOrder: (
    client: Client,
    vhost: string,
    driverJid: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    return measuredIqCaller.request(
      xml(
        "iq",
        { type: "set", to: `wizard@${vhost}/gps` },
        xml(
          "command",
          {
            node: "finalize_navi_order",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          // Backend reads only driver_jid now (pushes the route to GBOX as a
          // corridor). transport_id / selection are intentionally not sent.
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            naviField("driver_jid", driverJid),
          ),
        ),
      ),
    )
  },
  showDeliveryOrderExport: async (
    client: Client,
    deliveryOrderId: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())
    return measuredIqCaller.request(
      xml(
        "iq",
        { type: "set", to: `${WIZARD}@${vhost}/gps` },
        xml(
          "command",
          {
            node: "show_delivery_order_export",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "submit" },
            xml(
              "field",
              { var: "delivery_order_id" },
              xml("value", {}, deliveryOrderId),
            ),
          ),
        ),
      ),
    )
  },

  retryDeliveryOrderExport: async (
    client: Client,
    deliveryOrderId: string,
    overrides: {
      driverName?: string
      carPlates?: string
      trailerPlates?: string
      price?: string
      currency?: string
      referenceNumber?: string
    } = {},
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())
    const field = (varName: string, value: string) =>
      xml("field", { var: varName }, xml("value", {}, value))
    return measuredIqCaller.request(
      xml(
        "iq",
        { type: "set", to: `${WIZARD}@${vhost}/gps` },
        xml(
          "command",
          {
            node: "retry_delivery_order_export",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "submit" },
            field("delivery_order_id", deliveryOrderId),
            field("driver_name", overrides.driverName ?? ""),
            field("car_plates", overrides.carPlates ?? ""),
            field("trailer_plates", overrides.trailerPlates ?? ""),
            field("price", overrides.price ?? ""),
            field("currency", overrides.currency ?? ""),
            field("reference_number", overrides.referenceNumber ?? ""),
          ),
        ),
      ),
    )
  },

  // Mike's ad-hoc contract (gps `mbugno/delivery-order-api`, PR #188): reads
  // straight off `delivery_orders.metadata`, unlike show_delivery_order_export
  // which only knows the SLT-export snapshot — this is the only source for
  // nip/customer_name, and for a delivery_order id before any export attempt.
  showLatestDeliveryOrder: async (
    client: Client,
    driverJid: string,
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())
    return measuredIqCaller.request(
      xml(
        "iq",
        { type: "set", to: `${WIZARD}@${vhost}/gps` },
        xml(
          "command",
          {
            node: "show_latest_delivery_order",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "submit" },
            xml("field", { var: "driver_jid" }, xml("value", {}, driverJid)),
          ),
        ),
      ),
    )
  },

  // Omitting a field leaves it unchanged server-side rather than blanking it
  // (gps mbugno/delivery-order-api, _apply_meta). sourceFileUrl maps to
  // var "file_url" — Mike is extending update_delivery_order to accept it.
  updateDeliveryOrder: async (
    client: Client,
    orderId: string,
    fields: {
      referenceNumber?: string
      orderNumber?: string
      price?: string
      customerNip?: string
      customerName?: string
      driverName?: string
      carPlates?: string
      trailerPlates?: string
      coordinatorName?: string
      emptyKilometers?: string
      loadedKilometers?: string
      // "true" | "false" | "" (clears) — gps parses it into a bool.
      palletExchange?: string
      // Bare °C numbers as text; "" clears.
      temperatureMin?: string
      temperatureMax?: string
      sourceFileUrl?: string
    },
  ): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client.jid!.toString())
    const field = (varName: string, value: string) =>
      xml("field", { var: varName }, xml("value", {}, value))
    const optionalField = (varName: string, value: string | undefined) =>
      value === undefined ? [] : [field(varName, value)]
    return measuredIqCaller.request(
      xml(
        "iq",
        { type: "set", to: `${WIZARD}@${vhost}/gps` },
        xml(
          "command",
          {
            node: "update_delivery_order",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "submit" },
            field("id", orderId),
            ...optionalField("reference_number", fields.referenceNumber),
            ...optionalField("order_number", fields.orderNumber),
            ...optionalField("price", fields.price),
            ...optionalField("customer_nip", fields.customerNip),
            ...optionalField("customer_name", fields.customerName),
            ...optionalField("driver_name", fields.driverName),
            ...optionalField("car_plates", fields.carPlates),
            ...optionalField("trailer_plates", fields.trailerPlates),
            ...optionalField("coordinator_name", fields.coordinatorName),
            ...optionalField("empty_kilometers", fields.emptyKilometers),
            ...optionalField("loaded_kilometers", fields.loadedKilometers),
            ...optionalField("pallet_exchange", fields.palletExchange),
            ...optionalField("temperature_min", fields.temperatureMin),
            ...optionalField("temperature_max", fields.temperatureMax),
            ...optionalField("file_url", fields.sourceFileUrl),
          ),
        ),
      ),
    )
  },

  getPermissions: async (client: Client): Promise<UserPermissions> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const result = await measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: `wizard@${vhost}/account_config` },
        xml("command", {
          node: "get_permissions",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        }),
      ),
    )
    const fields =
      result.getChild("command")?.getChild("x")?.getChildren("field") ?? []
    const field = (name: string) =>
      fields.find((f: Element) => f.getAttr("var") === name)
    const managed = field("account_managed")?.getChildText("value")
    return {
      permissions:
        field("permissions")
          ?.getChildren("value")
          .map((v: Element) => v.getText()) ?? [],
      accountManaged: managed === "1" || managed === "true",
    }
  },

  /** Hand a chat to one dispatcher; an empty dispatcher takes it from all.
   * Resolves to the dispatchers who lost the chat. */
  assignChatDispatcher: async ({
    client,
    chatJid,
    dispatcherJid,
  }: {
    client: Client
    chatJid: string
    dispatcherJid: string
  }): Promise<string[]> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller, ASSIGN_CHAT_DISPATCHER_NODE)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const result = await measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: `wizard@${vhost}/account_config` },
        xml(
          "command",
          {
            node: ASSIGN_CHAT_DISPATCHER_NODE,
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "submit" },
            xml(
              "field",
              { var: "chat_jid", type: "text-single" },
              xml("value", {}, chatJid),
            ),
            xml(
              "field",
              { var: "dispatcher_jid", type: "text-single" },
              xml("value", {}, dispatcherJid),
            ),
          ),
        ),
      ),
    )
    const command = result.getChild("command")
    const form = command ? DataFormsParser.parse(command) : undefined
    return form?.fields?.["removed_from"]?.values.filter(Boolean) ?? []
  },

  changePassword: async (client: Client, userJid: string): Promise<string> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const result = await measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: `wizard@${vhost}/account_config` },
        xml(
          "command",
          {
            node: "change_password",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "user_jid", type: "text-single" },
              xml("value", {}, userJid),
            ),
          ),
        ),
      ),
    )
    const fields =
      result.getChild("command")?.getChild("x")?.getChildren("field") ?? []
    const passwordField = fields.find(
      (f: Element) => f.getAttr("var") === "new_password",
    )
    const password = passwordField?.getChildText("value") ?? ""
    if (!password) throw new Error("No password in change_password response")
    return password
  },

  setUserDisabled: async (
    client: Client,
    userJid: string,
    disabled: boolean,
  ): Promise<{ disabled: boolean; newPassword?: string }> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const result = await measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: `wizard@${vhost}/account_config` },
        xml(
          "command",
          {
            node: "set_user_disabled",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "user_jid", type: "jid-single" },
              xml("value", {}, userJid),
            ),
            xml(
              "field",
              { var: "disabled", type: "boolean" },
              xml("value", {}, disabled ? "1" : "0"),
            ),
          ),
        ),
      ),
    )
    const fields =
      result.getChild("command")?.getChild("x")?.getChildren("field") ?? []
    const disabledField = fields.find(
      (f: Element) => f.getAttr("var") === "disabled",
    )
    const passwordField = fields.find(
      (f: Element) => f.getAttr("var") === "new_password",
    )
    const disabledResult =
      (disabledField?.getChildText("value") ?? "false") !== "false"
    const newPassword = passwordField?.getChildText("value") || undefined
    return { disabled: disabledResult, newPassword }
  },

  upsertOfficeUser: ({
    client,
    username,
    firstName,
    lastName,
    phoneNumber,
    preferredLanguage,
  }: {
    client: Client
    username: string
    // Required for a new user. On an existing user an omitted field keeps
    // its stored value, so an edit can send only what changes.
    firstName?: string
    lastName?: string
    phoneNumber?: string
    preferredLanguage?: string
  }): Promise<Element> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const optionalFields = []
    if (firstName) {
      optionalFields.push(
        xml(
          "field",
          { var: "first_name", type: "text-single" },
          xml("value", {}, firstName),
        ),
      )
    }
    if (lastName) {
      optionalFields.push(
        xml(
          "field",
          { var: "last_name", type: "text-single" },
          xml("value", {}, lastName),
        ),
      )
    }
    if (phoneNumber) {
      optionalFields.push(
        xml(
          "field",
          { var: "phone_number", type: "text-single" },
          xml("value", {}, phoneNumber),
        ),
      )
    }
    if (preferredLanguage) {
      optionalFields.push(
        xml(
          "field",
          { var: "preferred_language", type: "text-single" },
          xml("value", {}, preferredLanguage),
        ),
      )
    }
    return measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: `wizard@${vhost}/account_config` },
        xml(
          "command",
          {
            node: "upsert_office_user",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "username", type: "text-single" },
              xml("value", {}, username),
            ),
            ...optionalFields,
          ),
        ),
      ),
    )
  },

  listGpsConfigs: async (client: Client): Promise<GpsConfigEntry[]> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const result = await measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: `wizard@${vhost}/gps` },
        xml("command", {
          node: "list_gps_configs",
          action: "execute",
          xmlns: "http://jabber.org/protocol/commands",
        }),
      ),
    )
    const command = result.getChild("command")
    const form = command ? DataFormsParser.parse(command) : undefined
    return (
      form?.items?.map((item) => ({
        id: item.fields["id"]?.values[0] ?? "",
        provider: item.fields["provider"]?.values[0] ?? "",
        name: item.fields["name"]?.values[0] ?? "",
        enabled: item.fields["enabled"]?.values[0] === "1",
        createdAt: item.fields["created_at"]?.values[0] ?? "",
        updatedAt: item.fields["updated_at"]?.values[0] ?? "",
        unreachableSince: item.fields["unreachable_since"]?.values[0] ?? "",
        plates: item.fields["plates"]?.values ?? [],
      })) ?? []
    )
  },

  /**
   * Step 1 of the two-step update_gps_provider adhoc.
   * Sends { id } and receives back a pre-filled form (enabled state +
   * empty credential fields) plus the sessionid needed for step 2.
   */
  fetchGpsProviderEditForm: async (
    client: Client,
    id: string,
  ): Promise<{
    sessionid: string
    enabled: boolean
    name: string
    credentialFields: { var: string; label: string }[]
  }> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const result = await measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: `wizard@${vhost}/gps` },
        xml(
          "command",
          {
            node: "update_gps_provider",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "submit" },
            xml(
              "field",
              { var: "id", type: "text-single" },
              xml("value", {}, id),
            ),
          ),
        ),
      ),
    )
    const command = result.getChild("command")
    if (!command)
      throw new Error(
        "No command element in update_gps_provider step-1 response",
      )
    const sessionid = command.getAttr("sessionid")
    if (!sessionid)
      throw new Error("No sessionid in update_gps_provider step-1 response")
    const form = DataFormsParser.parse(command)
    if (!form) throw new Error("No form in update_gps_provider step-1 response")
    const enabled =
      (form.fields["enabled"]?.values[0] ?? "true") !== "false" &&
      (form.fields["enabled"]?.values[0] ?? "true") !== "0"
    const name = form.fields["name"]?.values[0] ?? ""
    const credentialFields = Object.values(form.fields)
      .filter((f) => f.type === "text-private")
      .map((f) => ({ var: f.var!, label: f.label ?? f.var! }))
    return { sessionid, enabled, name, credentialFields }
  },

  /**
   * Step 2 of the two-step update_gps_provider adhoc.
   * Submits the edited form back using action="next" + the sessionid from step 1.
   */
  submitGpsProviderUpdate: async (
    client: Client,
    sessionid: string,
    name: string,
    enabled: boolean,
    credentials: Record<string, string>,
  ): Promise<void> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const credentialFields = Object.entries(credentials).map(([key, value]) =>
      xml("field", { var: key, type: "text-private" }, xml("value", {}, value)),
    )
    await measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: `wizard@${vhost}/gps` },
        xml(
          "command",
          {
            node: "update_gps_provider",
            action: "next",
            sessionid,
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "submit" },
            xml(
              "field",
              { var: "name", type: "text-single" },
              xml("value", {}, name),
            ),
            xml(
              "field",
              { var: "enabled", type: "boolean" },
              xml("value", {}, enabled ? "true" : "false"),
            ),
            ...credentialFields,
          ),
        ),
      ),
    )
  },

  /**
   * Step 1 of the two-step create_gps_provider adhoc.
   * Sends { provider, name } and receives back a form with one text-private
   * field per credential key (with human-readable labels) plus the sessionid.
   */
  initGpsProviderCreate: async (
    client: Client,
    provider: string,
    name: string,
  ): Promise<{
    sessionid: string
    credentialFields: { var: string; label: string }[]
  }> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const result = await measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: `wizard@${vhost}/gps` },
        xml(
          "command",
          {
            node: "create_gps_provider",
            action: "execute",
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "submit" },
            xml(
              "field",
              { var: "provider", type: "text-single" },
              xml("value", {}, provider),
            ),
            xml(
              "field",
              { var: "name", type: "text-single" },
              xml("value", {}, name),
            ),
          ),
        ),
      ),
    )
    const command = result.getChild("command")
    if (!command)
      throw new Error(
        "No command element in create_gps_provider step-1 response",
      )
    const sessionid = command.getAttr("sessionid")
    if (!sessionid)
      throw new Error("No sessionid in create_gps_provider step-1 response")
    const form = DataFormsParser.parse(command)
    if (!form) throw new Error("No form in create_gps_provider step-1 response")
    const credentialFields = Object.values(form.fields)
      .filter((f) => f.type === "text-private")
      .map((f) => ({ var: f.var!, label: f.label ?? f.var! }))
    return { sessionid, credentialFields }
  },

  /**
   * Step 2 of the two-step create_gps_provider adhoc.
   * Submits the credentials using action="next" + the sessionid from step 1.
   */
  submitGpsProviderCreate: async (
    client: Client,
    sessionid: string,
    credentials: Record<string, string>,
  ): Promise<void> => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller)
    const vhost = extractVhostFromJid(client!.jid!.toString())
    const credentialFields = Object.entries(credentials).map(([key, value]) =>
      xml("field", { var: key, type: "text-private" }, xml("value", {}, value)),
    )
    await measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: `wizard@${vhost}/gps` },
        xml(
          "command",
          {
            node: "create_gps_provider",
            action: "next",
            sessionid,
            xmlns: "http://jabber.org/protocol/commands",
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "submit" },
            ...credentialFields,
          ),
        ),
      ),
    )
  },
}

export type GpsConfigEntry = {
  id: string
  provider: string
  name: string
  enabled: boolean
  createdAt: string
  updatedAt: string
  unreachableSince: string
  plates: string[]
}

export default AdHocCommands
